import { Component } from '@theme/component';

const DISMISS_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * @typedef {{year: number, month: number, day: number}} DateParts
 */

/**
 * @typedef {{days: number, hours: number, minutes: number, seconds: number}} CountdownParts
 */

/**
 * @param {string | number | undefined | null} value
 * @param {number} [fallback=0]
 */
const parseNumber = (value, fallback = 0) => {
  const parsed = parseInt(`${value}`.trim(), 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

/** @param {string | number | undefined | null} value */
const parseMinute = (value) => {
  const parsed = parseNumber(value, -1);
  if (parsed < 0 || parsed > 59) return null;
  return parsed;
};

/**
 * @param {string | undefined | null} value
 * @returns {DateParts | null}
 */
const parseDateParts = (value) => {
  const normalized = `${value}`.trim();
  const usDate = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usDate) {
    const year = parseNumber(usDate[3]);
    const month = parseNumber(usDate[1]);
    const day = parseNumber(usDate[2]);

    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    return { year, month, day };
  }

  let match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    match = normalized.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  }

  if (!match) return null;

  const year = parseNumber(match[1]);
  const month = parseNumber(match[2]);
  const day = parseNumber(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return { year, month, day };
};

/** @param {HTMLElement} container */
const parseDatePartsFromDataset = (container) => {
  const year = parseNumber(container.dataset.endYear, 0);
  const month = parseNumber(container.dataset.endMonth, 0);
  const day = parseNumber(container.dataset.endDay, 0);

  if (year > 0 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
    return { year, month, day };
  }

  return parseDateParts(container.dataset.endDate || '');
};

/** @param {string | undefined | null} timeZone */
const resolveTimeZone = (timeZone) => {
  const normalized = `${timeZone || ''}`.trim();
  if (!normalized) return 'UTC';

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: normalized });
    return normalized;
  } catch {
    return 'UTC';
  }
};

/** @param {string} timeZone */
const getFormatter = (timeZone) => {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

/**
 * @param {number} epochMs
 * @param {string} timeZone
 */
const getTimeZoneOffsetMs = (epochMs, timeZone) => {
  const formatter = getFormatter(timeZone);
  const parts = formatter.formatToParts(new Date(epochMs));

  /** @type {Record<string, string>} */
  const map = {};

  parts.forEach((part) => {
    if (part.type !== 'literal') {
      map[part.type] = part.value;
    }
  });

  const asUtc = Date.UTC(
    parseNumber(map.year),
    parseNumber(map.month) - 1,
    parseNumber(map.day),
    parseNumber(map.hour),
    parseNumber(map.minute),
    parseNumber(map.second)
  );

  return asUtc - epochMs;
};

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @param {number} hour24
 * @param {number} minute
 * @param {string} timeZone
 */
const toUtcTimestamp = (year, month, day, hour24, minute, timeZone) => {
  const utcGuess = Date.UTC(year, month - 1, day, hour24, minute, 0);
  const firstOffset = getTimeZoneOffsetMs(utcGuess, timeZone);
  const firstPass = utcGuess - firstOffset;
  const secondOffset = getTimeZoneOffsetMs(firstPass, timeZone);

  if (firstOffset !== secondOffset) {
    return utcGuess - secondOffset;
  }

  return firstPass;
};

/**
 * @param {number} hour
 * @param {string} period
 */
const convertTo24Hour = (hour, period) => {
  if (period === 'pm' && hour !== 12) return hour + 12;
  if (period === 'am' && hour === 12) return 0;
  return hour;
};

/** @param {number} value */
const formatPart = (value) => `${Math.max(0, value)}`.padStart(2, '0');

/**
 * @param {number} secondsRemaining
 * @returns {CountdownParts}
 */
const getTimeParts = (secondsRemaining) => {
  const days = Math.floor(secondsRemaining / 86400);
  const hours = Math.floor((secondsRemaining % 86400) / 3600);
  const minutes = Math.floor((secondsRemaining % 3600) / 60);
  const seconds = secondsRemaining % 60;

  return { days, hours, minutes, seconds };
};

/** @param {Element} slide */
const hideAnnouncementBar = (slide) => {
  const announcement = slide.closest('.announcement-bar');
  if (announcement instanceof HTMLElement) {
    announcement.hidden = true;
  }
};

/** @param {Element} announcementBar */
const getVisibleSlides = (announcementBar) => {
  return Array.from(announcementBar.querySelectorAll('[ref="slides[]"]')).filter((slide) => {
    if (!(slide instanceof HTMLElement)) return false;
    return !slide.hasAttribute('data-announcement-hidden') && !slide.hidden;
  });
};

/** @param {HTMLElement} slide */
const hideSlide = (slide) => {
  if (slide.hasAttribute('data-announcement-hidden')) return;

  slide.setAttribute('data-announcement-hidden', 'true');
  slide.setAttribute('aria-hidden', 'true');
  slide.hidden = true;

  const announcementBar = slide.closest('announcement-bar-component');
  if (!announcementBar) {
    hideAnnouncementBar(slide);
    return;
  }

  const visibleSlides = getVisibleSlides(announcementBar);

  if (visibleSlides.length === 0) {
    hideAnnouncementBar(slide);
    return;
  }

  if (announcementBar instanceof HTMLElement && 'current' in announcementBar) {
    const currentIndex = announcementBar.current;
    if (typeof currentIndex === 'number') {
      announcementBar.current = currentIndex;
    }
  }
};

/**
 * @param {HTMLElement} container
 * @param {number} secondsRemaining
 */
const setCountdownValues = (container, secondsRemaining) => {
  const values = getTimeParts(secondsRemaining);

  container.querySelectorAll('[data-countdown-part]').forEach((part) => {
    const key = part.getAttribute('data-countdown-part');
    if (key !== 'days' && key !== 'hours' && key !== 'minutes' && key !== 'seconds') return;

    part.textContent = formatPart(values[key]);
  });
};

/** @param {HTMLElement} container */
const buildTargetTimestamp = (container) => {
  const date = parseDatePartsFromDataset(container);
  const hour = parseNumber(container.dataset.endHour, 0);
  const minute = parseMinute(container.dataset.endMinute || '');
  const period = `${container.dataset.endPeriod || 'am'}`.toLowerCase();
  const timeZone = resolveTimeZone(container.dataset.timezone || 'UTC');

  if (!date || minute === null || hour < 1 || hour > 12) return null;
  if (period !== 'am' && period !== 'pm') return null;

  const hour24 = convertTo24Hour(hour, period);

  return toUtcTimestamp(date.year, date.month, date.day, hour24, minute, timeZone);
};

const isDesignMode = () => {
  return Boolean(window.Shopify && window.Shopify.designMode);
};

/** @param {string | undefined} key */
const toScopedDismissKey = (key) => {
  if (!key) return undefined;

  const pageScope = window.location.pathname || '/';
  return `${key}:${pageScope}`;
};

/** @param {string | undefined} key */
const readDismissUntil = (key) => {
  const scopedKey = toScopedDismissKey(key);
  if (!scopedKey || isDesignMode()) return null;

  try {
    const value = window.localStorage.getItem(scopedKey);
    if (!value) return null;

    const dismissUntil = parseNumber(value, 0);
    if (dismissUntil <= 0) return null;

    return dismissUntil;
  } catch {
    return null;
  }
};

/**
 * @param {string | undefined} key
 * @param {number} dismissUntil
 */
const writeDismissUntil = (key, dismissUntil) => {
  const scopedKey = toScopedDismissKey(key);
  if (!scopedKey || isDesignMode()) return;

  try {
    window.localStorage.setItem(scopedKey, `${dismissUntil}`);
  } catch {
    // Ignore quota/privacy storage errors.
  }
};

class AnnouncementCountdown extends HTMLElement {
  /** @type {number | undefined} */
  #interval = undefined;

  #canPersistDismiss = false;

  connectedCallback() {
    this.setAttribute('data-countdown-mounted', 'true');

    const slide = this.closest('[ref="slides[]"]');
    if (!(slide instanceof HTMLElement)) {
      this.setAttribute('data-countdown-error', 'missing-slide');
      return;
    }

    const closeButton = this.querySelector('[data-countdown-close]');
    this.#canPersistDismiss = Boolean(closeButton);
    closeButton?.addEventListener('click', this.#onCloseClick);

    const dismissKey = this.dataset.dismissKey;
    const dismissUntil = this.#canPersistDismiss ? readDismissUntil(dismissKey) : null;
    if (dismissUntil && dismissUntil > Date.now()) {
      this.setAttribute('data-countdown-state', 'dismissed');
      hideSlide(slide);
      return;
    }

    const targetTimestamp = buildTargetTimestamp(this);
    if (!targetTimestamp) {
      console.error('countdown invalid target', {dataset: {...this.dataset}});
      this.setAttribute('data-countdown-error', 'invalid-target');
      hideSlide(slide);
      return;
    }

    const tick = () => {
      const secondsRemaining = Math.floor((targetTimestamp - Date.now()) / 1000);

      if (secondsRemaining <= 0) {
        setCountdownValues(this, 0);
        this.setAttribute('data-countdown-state', 'expired');
        hideSlide(slide);
        this.#suspend();
        return;
      }

      this.setAttribute('data-countdown-state', 'running');
      setCountdownValues(this, secondsRemaining);
    };

    tick();
    this.#interval = setInterval(tick, 1000);
  }

  disconnectedCallback() {
    const closeButton = this.querySelector('[data-countdown-close]');
    closeButton?.removeEventListener('click', this.#onCloseClick);
    this.#suspend();
  }

  #suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #onCloseClick = () => {
    const slide = this.closest('[ref="slides[]"]');
    if (!(slide instanceof HTMLElement)) return;

    if (this.#canPersistDismiss) {
      const dismissUntil = Date.now() + DISMISS_TTL_MS;
      writeDismissUntil(this.dataset.dismissKey, dismissUntil);
    }

    hideSlide(slide);
    this.#suspend();
  };
}

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const visibleSlides = (this.refs.slides ?? []).filter((slide) => {
      return !slide.hasAttribute('data-announcement-hidden') && !slide.hidden;
    });

    if (visibleSlides.length === 0) return;

    let relativeIndex = current % visibleSlides.length;
    if (relativeIndex < 0) {
      relativeIndex += visibleSlides.length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${slide !== visibleSlides[relativeIndex]}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
